interface KPIMetricProps {
  icon: string
  label: string
  value: string | number
  previousValue?: number
  currentValue?: number
  format?: 'number' | 'currency' | 'percent' | 'duration'
  inverted?: boolean // For metrics where lower is better (bounce rate)
  suffix?: string
  isLive?: boolean // For the Online indicator
}

function formatValue(
  value: string | number,
  format?: 'number' | 'currency' | 'percent' | 'duration',
  suffix?: string
): string {
  if (typeof value === 'string') return value + (suffix || '')

  switch (format) {
    case 'currency':
      if (value >= 1000) {
        return `$${(value / 1000).toFixed(1)}k`
      }
      return `$${value.toFixed(2)}`

    case 'percent':
      return `${value.toFixed(1)}%`

    case 'duration':
      if (value < 60) return `${Math.round(value)}s`
      const minutes = Math.floor(value / 60)
      const seconds = Math.round(value % 60)
      if (minutes < 60) return `${minutes}m ${seconds}s`
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      return `${hours}h ${remainingMinutes}m`

    case 'number':
    default:
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`
      }
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}k`
      }
      return value.toLocaleString()
  }
}

function calculateChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0
  return ((current - previous) / previous) * 100
}

export function KPIMetric({
  icon,
  label,
  value,
  previousValue,
  currentValue,
  format,
  inverted = false,
  suffix,
  isLive = false,
}: KPIMetricProps) {
  const formattedValue = formatValue(value, format, suffix)

  // Calculate trend if we have comparison values
  let trend: 'up' | 'down' | 'neutral' | null = null
  let changePercent: number | null = null

  if (previousValue !== undefined && currentValue !== undefined) {
    changePercent = calculateChange(currentValue, previousValue)
    if (Math.abs(changePercent) < 0.1) {
      trend = 'neutral'
    } else {
      trend = changePercent > 0 ? 'up' : 'down'
    }
  }

  // Determine if trend is good or bad (inverted for bounce rate where lower is better)
  const isPositive = inverted ? trend === 'down' : trend === 'up'

  return (
    <div className="kpi-card">
      <span className="text-lg mb-1">{icon}</span>
      <div className="kpi-value">{formattedValue}</div>
      <div className="kpi-label">{label}</div>
      {trend && changePercent !== null && (
        <div className={isPositive ? 'kpi-trend-up' : trend === 'neutral' ? 'kpi-trend text-gray-500' : 'kpi-trend-down'}>
          {trend === 'up' && (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          )}
          {trend === 'down' && (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          )}
          <span>{Math.abs(changePercent).toFixed(1)}%</span>
        </div>
      )}
      {isLive && (
        <div className="flex items-center gap-1 mt-1">
          <span className="online-dot" />
          <span className="text-xs text-green-600 font-medium">Live</span>
        </div>
      )}
    </div>
  )
}
